import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './UltraMemberOptions.module.scss';
import { centsToDollars } from '../../../lib/util';
import type { BuyingFor } from '../WhoAreYouShoppingFor/WhoAreYouShoppingFor';
import type { UltraEligiblePet } from '../../../hooks/useUltraEligiblePets';
import { ReactComponent as IconChevron } from '../../../assets/images/icons/chevron_icon.svg';

interface UltraMemberOptionsProps {
  buyingFor: BuyingFor | null;
  onSelectBuyingFor: (choice: BuyingFor) => void;
  /**
   * Whether to show the "device only" (existing membership) card. False when the member has no pet on
   * an active S3/S3+/Mini membership — the card is hidden and only the new-membership option remains.
   */
  showExistingOption: boolean;
  /** Logged-in member's eligible pets — used for the profile-photo chips. */
  pets: UltraEligiblePet[];
  /** Members hardware price (no new membership) — product.pricingInCents. */
  membersPriceInCents: number;
  /** Discounted device price when bundled with a new membership — standalonePricingInCents. */
  newDevicePriceInCents: number;
  /** Selected (1-year) membership price, once loaded. */
  membershipPriceInCents?: number;
}

const formatPrice = (cents: number) => `$${centsToDollars(cents).replace(/\.00$/, '')}`;

// Max avatar chips shown before the remaining pets collapse into a "+N" chip.
const MAX_AVATARS = 3;

/**
 * Logged-in member view of the Ultra PDP. Defaults to the "use your existing membership" path
 * ($299 one-time, no new membership). Expanding the "new dog" card switches to the new-membership
 * flow, which the parent's addToBag logic already handles via `buyingFor`.
 */
export default function UltraMemberOptions({
  buyingFor,
  onSelectBuyingFor,
  showExistingOption,
  pets,
  membersPriceInCents,
  newDevicePriceInCents,
  membershipPriceInCents,
}: UltraMemberOptionsProps) {
  const { t } = useTranslation();

  const isNew = buyingFor === 'new';
  const savingsInCents = membersPriceInCents - newDevicePriceInCents;

  // Shared label for the new-membership card header — used whether the header is an interactive
  // toggle (when the existing-membership option is also present) or static (when it's the only option).
  const newDogHeaderText = (
    <span className={styles.newDogHeaderText}>
      <span className={styles.newDogTitle}>{t('ultra_buying_for_new_dog')}</span>
      {!isNew && <span className={styles.newDogSubtitle}>{t('ultra_buying_for_new_dog_subtitle')}</span>}
    </span>
  );

  return (
    <div className={styles.memberSection}>
      {showExistingOption && (
        <button
          type="button"
          className={classNames(styles.membersCard, { [styles.selected]: !isNew })}
          onClick={() => onSelectBuyingFor('existing')}
          aria-pressed={!isNew}
        >
          <div className={styles.membersHeader}>
            <span className={styles.hardwareName}>{t('ultra_hardware_kit_name')}</span>
            <span className={styles.membersBadge}>{t('ultra_members_price')}</span>
          </div>
          <div className={styles.priceRow}>
            <span className={styles.price}>{formatPrice(membersPriceInCents)}</span>
            <span className={styles.oneTime}>{t('ultra_one_time')}</span>
          </div>
          <div className={styles.divider} />
          <div className={styles.noMembershipRow}>
            {pets.length > 0 && (
              <div className={styles.petAvatars}>
                {pets.slice(0, MAX_AVATARS).map((pet) => (
                  <span key={pet.petId} className={styles.petAvatar}>
                    {pet.photoUrl ? (
                      <img src={pet.photoUrl} alt={pet.name} className={styles.petAvatarImg} />
                    ) : (
                      <span className={styles.petAvatarFallback}>{pet.name.charAt(0)}</span>
                    )}
                  </span>
                ))}
                {pets.length > MAX_AVATARS && (
                  <span className={classNames(styles.petAvatar, styles.petAvatarCount)}>
                    {`+${pets.length - MAX_AVATARS}`}
                  </span>
                )}
              </div>
            )}
            <p className={styles.noMembershipText}>{t('ultra_member_no_membership_needed')}</p>
          </div>
        </button>
      )}

      <div className={classNames(styles.newDogCard, { [styles.selected]: isNew })}>
        {showExistingOption ? (
          <button
            type="button"
            className={styles.newDogHeader}
            onClick={() => onSelectBuyingFor(isNew ? 'existing' : 'new')}
            aria-expanded={isNew}
          >
            {newDogHeaderText}
            <IconChevron className={classNames(styles.chevron, { [styles.chevronOpen]: isNew })} />
          </button>
        ) : (
          // Only option available — render the header as static text, not a focusable no-op control.
          <div className={styles.newDogHeader}>{newDogHeaderText}</div>
        )}
        <div className={classNames(styles.newDogBodyWrapper, { [styles.expanded]: isNew })} aria-hidden={!isNew}>
          <div className={styles.newDogBody}>
            <div className={styles.newDogBodyInner}>
              <p className={styles.saveCallout}>
                {t('ultra_new_dog_save_callout', { savings: formatPrice(savingsInCents) })}
              </p>
              {/* Render the breakdown only once the membership price has loaded, so the subtotal
                  never briefly shows a device-only (too-low) total. */}
              {membershipPriceInCents != null && (
                <div className={styles.breakdown}>
                  <div className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>{t('ultra_fi_ultra_device')}</span>
                    <span className={styles.breakdownValue}>
                      <span className={styles.strikethrough}>{formatPrice(membersPriceInCents)}</span>{' '}
                      {formatPrice(newDevicePriceInCents)}
                    </span>
                  </div>
                  <div className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>{t('ultra_one_year_membership')}</span>
                    <span className={styles.breakdownValue}>{formatPrice(membershipPriceInCents)}</span>
                  </div>
                  <div className={classNames(styles.breakdownRow, styles.subtotalRow)}>
                    <span className={styles.breakdownLabel}>{t('subtotal')}</span>
                    <span className={styles.breakdownValue}>
                      {formatPrice(newDevicePriceInCents + membershipPriceInCents)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
